import { useOpenAppRoute } from '@hooks/useOpenAppRoute';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { SchedulerGridScheduleMenuProps } from './types';
import { cn } from '@utils/cn';
import { Menu } from '@components/ui/menu';
import { useState } from 'react';
import { useLocation } from 'react-router';
import { formatTime, parseRoute } from '@utils/i18n';
import { appRoutes } from '@utils/consts';
import { useTranslation } from 'react-i18next';
import { RemoveConfirmationAlert } from '@components/dialogs/removeConfirmationAlert';
import { useToast } from '@hooks/useToast';
import { useRemoveInternalSchedule } from '@services/schedules/internal/remove';
import { MenuItem } from '@components/ui/menu/types';
import { useQueryParams } from '@hooks/queryParams';
import { useWorkload } from '@contexts/workload';
import { PublishScheduleConfirmationDialog } from '@components/schedules/dialogs/internal/publish/confirmation';
import { usePublishInternalSchedules } from '@services/schedules/internal/publish';
import { formatDate } from '@utils/dateHelpers';
import { useSchedulerContext } from '@contexts/scheduler';

export const SchedulerGridScheduleMenu = ({
  data,
  className,
  reschedulable = true,
}: SchedulerGridScheduleMenuProps) => {
  const { t } = useTranslation('app');
  const { toast } = useToast();
  const openAppRoute = useOpenAppRoute();
  const [open, setOpen] = useState(false);
  const [isRemove, setIsRemove] = useState(false);
  const [isSend, setIsSend] = useState(false);
  const { pathname } = useLocation();
  const { modal } = useSchedulerContext();

  const {
    params: { type },
  } = useQueryParams();

  const isDailyWorkload = pathname.includes(appRoutes.workload) && type === 'day';

  const workloadContext = useWorkload();

  const { id, task, routePoint, startsAt, endsAt, foreman, status, conflicts } = data;
  const jobId = task?.job?.id || '';
  const projectId = task?.job?.projectId || '';
  const stageId = task?.stage?.id || '';

  const remove = useRemoveInternalSchedule({
    id,
    jobId,
    callback: () => {
      toast({ title: t('messages.success.schedule.remove') });
    },
  });

  const send = usePublishInternalSchedules({
    callback: () => {
      toast({ title: t('messages.success.schedule.send') });
    },
  });

  const handleReschedule = () => {
    if (!task || !task.job || !workloadContext) return;
    const { job } = task;
    const { setState } = workloadContext;

    setState((prev) => ({
      ...prev,
      rescheduler: {
        open: true,
        initialDate: startsAt,
        initialParams: {
          users: [foreman?.id],
          specialties: foreman?.specialties?.map((el) => el.value),
        },
        job: {
          id: job.id,
          name: job.name || '',
          projectId: job.projectId || '',
          address: job.address || '',
          project: job.project && {
            name: job.project.name || '',
            address: job.project.address || '',
          },
          division: job.division || { id: '', name: '' },
        },
        task: {
          id: task.id,
          name: task.name,
          scheduleType: task.scheduleType,
          taskType: task.taskType,
          dateConfirmation: task.dateConfirmation,
          note: task.note,
          schedule: {
            ...data,
            id: id || '',
            task: {
              ...task,
              name: task.name || '',
            },
            foreman: foreman ? { id: foreman.id, specialty: [foreman?.specialties] } : { id: '' },
          },
          foremanSpecialty: task.foremanSpecialty,
          status: task.status,
          appearance: task.appearance,
        },
      },
    }));
  };

  return (
    <>
      <Menu
        variant="small"
        open={open}
        setOpen={setOpen}
        options={[
          {
            group: {
              id: 'scheduler-schedule-menu',
            },
            items: [
              isDailyWorkload &&
                !modal &&
                !routePoint &&
                reschedulable &&
                jobId &&
                stageId &&
                task?.foremanSpecialty && {
                  id: 'reschedule',
                  name: t('actions.reschedule'),
                  callback: handleReschedule,
                },
              jobId &&
                stageId && {
                  id: 'viewDetails',
                  name: t('actions.viewDetails'),
                  callback: () =>
                    openAppRoute(
                      `/${parseRoute(
                        appRoutes.jobWorkProgress(projectId, jobId),
                      )}?open=${stageId}&focus=${stageId}`,
                    ),
                },
              !routePoint &&
                reschedulable &&
                !modal && {
                  id: 'removeSchedule',
                  name: t('actions.remove'),
                  callback: () => setIsRemove(!isRemove),
                },
              !routePoint &&
                !modal &&
                status === 'draft' &&
                foreman?.id && {
                  id: 'sendSchedule',
                  name: t('actions.send'),
                  callback: () => setIsSend(!isSend),
                },
            ].filter(Boolean) as MenuItem[],
          },
        ]}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn([
            'absolute right-0 z-10 flex h-4 w-4 items-center justify-center rounded hover:border',
            open && 'border',
            className,
          ])}>
          <SvgConsumer id="menu" />
        </button>
      </Menu>
      <RemoveConfirmationAlert
        open={isRemove}
        setOpen={setIsRemove}
        title={t('schedules.removeTitle')}
        description={t('schedules.removeInfo')}
        onConfirm={remove.mutate}
        locked={remove.isLoading}
      />
      <PublishScheduleConfirmationDialog
        open={isSend}
        setOpen={setIsSend}
        onConfirm={() => void send.mutate({ schedulesIds: [id], confirmed: true })}
        disabled={send.isLoading}
        date={formatDate(new Date(startsAt), 'MMM do')}
        uniqueForemenIds={[foreman?.id || '']}
        crewsWithPhoneIds={[]}
        crewsWithoutPhoneIds={[]}
        fieldUserAsCrewIds={[]}
        conflicts={conflicts || []}
        name={task?.name}
        customTitle
        summaryDetail={`${[formatTime(new Date(startsAt)), formatTime(new Date(endsAt || ''))].join(
          ' - ',
        )}, ${formatDate(new Date(startsAt), 'MMM do, yyyy')}`}
      />
    </>
  );
};
